import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ROLE_OPTIONS = ['owner', 'admin', 'translator', 'viewer'] as const;
type Role = (typeof ROLE_OPTIONS)[number];

interface Props {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: {
    userSubject: string;
    email?: string;
    displayName?: string;
    role: Role;
  }) => Promise<void> | void;
}

export function InviteMemberDialog({ open, pending, onClose, onSubmit }: Props) {
  const [userSubject, setUserSubject] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('translator');

  useEffect(() => {
    if (!open) {
      setUserSubject('');
      setEmail('');
      setDisplayName('');
      setRole('translator');
    }
  }, [open]);

  const canSubmit = userSubject.trim().length > 0 && !pending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invite member"
      description="Identify the user by their Keycloak subject id; role is workspace-wide."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              void onSubmit({
                userSubject: userSubject.trim(),
                email: email.trim() || undefined,
                displayName: displayName.trim() || undefined,
                role,
              })
            }
            disabled={!canSubmit}
          >
            {pending ? 'Inviting…' : 'Invite'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) {
            void onSubmit({
              userSubject: userSubject.trim(),
              email: email.trim() || undefined,
              displayName: displayName.trim() || undefined,
              role,
            });
          }
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="invite-subject">Keycloak subject</Label>
          <Input
            id="invite-subject"
            value={userSubject}
            onChange={(e) => setUserSubject(e.target.value)}
            placeholder="e.g. f6e4… (sub claim from Keycloak)"
            autoFocus
            required
          />
          <p className="text-[11px] text-muted-foreground">
            Find it in Keycloak Admin → Users → User details. We'll switch to email-based
            lookup once the realm self-serve flow is ready.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="invite-email">Email (optional)</Label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="diego@criptalia.it"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="invite-name">Display name (optional)</Label>
          <Input
            id="invite-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Diego Dal Cero"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="invite-role">Role</Label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
